import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit2, Trash2, X, Search, CheckCircle, XCircle } from 'lucide-react';
import { proceduresApi, locationsApi } from '@/api/client';
import { toast } from '@/utils/toast';

const procedureSchema = z.object({
    code: z.string().min(1, 'Code is required'),
    name: z.string().min(1, 'Name is required'),
    category: z.string().optional(),
    description: z.string().optional(),
    defaultStayDays: z.number().int().positive().default(1),
    preOpDays: z.number().int().nonnegative().default(0),
    postOpDays: z.number().int().nonnegative().default(0),
    defaultIcuDays: z.number().int().nonnegative().default(0),
});

type ProcedureFormData = z.infer<typeof procedureSchema>;

export function ProceduresPage() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProcedure, setEditingProcedure] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const queryClient = useQueryClient();

    const { data: procedures = [], isLoading } = useQuery({
        queryKey: ['procedures'],
        queryFn: () => proceduresApi.list(),
    });

    const { data: locations = [] } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const createMutation = useMutation({
        mutationFn: proceduresApi.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['procedures'] });
            toast.success('Procedure created successfully');
            setIsModalOpen(false);
            setEditingProcedure(null);
            reset({
                code: '',
                name: '',
                category: '',
                description: '',
                defaultStayDays: 1,
                preOpDays: 0,
                postOpDays: 0,
                defaultIcuDays: 0,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to create procedure');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<ProcedureFormData> }) =>
            proceduresApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['procedures'] });
            toast.success('Procedure updated successfully');
            setIsModalOpen(false);
            setEditingProcedure(null);
            reset({
                code: '',
                name: '',
                category: '',
                description: '',
                defaultStayDays: 1,
                preOpDays: 0,
                postOpDays: 0,
                defaultIcuDays: 0,
            });
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to update procedure');
        },
    });

    const configureMutation = useMutation({
        mutationFn: ({ id, locationId, isActive }: { id: string; locationId: string; isActive: boolean }) =>
            proceduresApi.configure(id, locationId, isActive),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['procedures'] });
        },
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<ProcedureFormData>({
        resolver: zodResolver(procedureSchema),
        defaultValues: editingProcedure || {
            defaultStayDays: 1,
            preOpDays: 0,
            postOpDays: 0,
            defaultIcuDays: 0,
        },
    });

    const onSubmit = (data: ProcedureFormData) => {
        if (editingProcedure) {
            updateMutation.mutate({ id: editingProcedure.id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleEdit = (procedure: any) => {
        setEditingProcedure(procedure);
        reset(procedure);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingProcedure(null);
        reset({
            defaultStayDays: 1,
            preOpDays: 0,
            postOpDays: 0,
            defaultIcuDays: 0,
        });
    };

    const toggleLocationConfig = (procedureId: string, locationId: string, currentlyActive: boolean) => {
        configureMutation.mutate({
            id: procedureId,
            locationId,
            isActive: !currentlyActive,
        });
    };

    // Filter procedures
    const categories = Array.from(new Set(procedures.map((p: any) => p.category as string).filter(Boolean))) as string[];
    const filteredProcedures = procedures.filter((p: any) => {
        const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.code.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = !selectedCategory || p.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Procedures</h1>
                    <p className="text-slate-600">Manage surgical procedures catalog</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="btn btn-primary btn-md"
                >
                    <Plus className="w-4 h-4" />
                    Add Procedure
                </button>
            </div>

            {/* Filters */}
            <div className="card">
                <div className="card-body">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                placeholder="Search by name or code..."
                                className="input pl-10"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                        <select
                            className="input w-full sm:w-48"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="">All categories</option>
                            {categories.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Procedures table */}
            <div className="card">
                {isLoading ? (
                    <div className="card-body">
                        <div className="animate-pulse space-y-4">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="h-16 bg-slate-100 rounded-lg" />
                            ))}
                        </div>
                    </div>
                ) : filteredProcedures.length === 0 ? (
                    <div className="card-body text-center py-16">
                        <h3 className="text-lg font-medium text-slate-900">No procedures found</h3>
                        <p className="text-slate-500 mt-1">Add your first procedure to get started.</p>
                        <button onClick={() => setIsModalOpen(true)} className="btn btn-primary btn-md mt-4">
                            Add Procedure
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Name</th>
                                    <th>Category</th>
                                    <th>Stay Days</th>
                                    <th>Locations</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProcedures.map((procedure: any) => (
                                    <tr key={procedure.id}>
                                        <td className="font-mono text-sm">{procedure.code}</td>
                                        <td>
                                            <div>
                                                <p className="font-medium">{procedure.name}</p>
                                                {procedure.description && (
                                                    <p className="text-xs text-slate-500 truncate max-w-xs">
                                                        {procedure.description}
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {procedure.category && (
                                                <span className="badge badge-primary">{procedure.category}</span>
                                            )}
                                        </td>
                                        <td className="text-sm">
                                            {procedure.defaultStayDays} days
                                            {(procedure.preOpDays > 0 || procedure.postOpDays > 0 || procedure.defaultIcuDays > 0) && (
                                                <span className="text-xs text-slate-500 block">
                                                    ({procedure.preOpDays} pre / {procedure.postOpDays} post{procedure.defaultIcuDays > 0 ? ` / ${procedure.defaultIcuDays} ICU` : ''})
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="flex flex-wrap gap-1">
                                                {locations.map((location: any) => {
                                                    const config = procedure.configs?.find(
                                                        (c: any) => c.locationId === location.id
                                                    );
                                                    const isActive = config?.isActive;
                                                    return (
                                                        <button
                                                            key={location.id}
                                                            onClick={() => toggleLocationConfig(procedure.id, location.id, !!isActive)}
                                                            className={`text-xs px-2 py-1 rounded-md transition-colors ${isActive
                                                                ? 'bg-success-50 text-success-700 hover:bg-success-100'
                                                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                                }`}
                                                            title={isActive ? 'Click to disable' : 'Click to enable'}
                                                        >
                                                            {isActive ? (
                                                                <CheckCircle className="w-3 h-3 inline mr-1" />
                                                            ) : (
                                                                <XCircle className="w-3 h-3 inline mr-1" />
                                                            )}
                                                            {location.name}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleEdit(procedure)}
                                                className="btn btn-ghost btn-sm"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
                    <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {editingProcedure ? 'Edit Procedure' : 'Add New Procedure'}
                            </h2>
                            <button onClick={handleCloseModal} className="p-1 hover:bg-slate-100 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="card-body space-y-4">
                                {/* Code */}
                                <div>
                                    <label htmlFor="code" className="label">
                                        Procedure Code *
                                    </label>
                                    <input
                                        id="code"
                                        type="text"
                                        className={`input ${errors.code ? 'input-error' : ''}`}
                                        placeholder="e.g., CABG-001"
                                        {...register('code')}
                                    />
                                    {errors.code && <p className="error-text">{errors.code.message}</p>}
                                </div>

                                {/* Name */}
                                <div>
                                    <label htmlFor="name" className="label">
                                        Procedure Name *
                                    </label>
                                    <input
                                        id="name"
                                        type="text"
                                        className={`input ${errors.name ? 'input-error' : ''}`}
                                        placeholder="e.g., Coronary Artery Bypass Grafting"
                                        {...register('name')}
                                    />
                                    {errors.name && <p className="error-text">{errors.name.message}</p>}
                                </div>

                                {/* Category */}
                                <div>
                                    <label htmlFor="category" className="label">
                                        Category
                                    </label>
                                    <input
                                        id="category"
                                        type="text"
                                        className="input"
                                        placeholder="e.g., Cardiac Surgery"
                                        {...register('category')}
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <label htmlFor="description" className="label">
                                        Description
                                    </label>
                                    <textarea
                                        id="description"
                                        rows={3}
                                        className="input"
                                        placeholder="Brief description of the procedure..."
                                        {...register('description')}
                                    />
                                </div>

                                {/* Stay Days */}
                                <div className="grid grid-cols-4 gap-4">
                                    <div>
                                        <label htmlFor="defaultStayDays" className="label">
                                            Hospital Stay (days)
                                        </label>
                                        <input
                                            id="defaultStayDays"
                                            type="number"
                                            min="1"
                                            className="input"
                                            {...register('defaultStayDays', { valueAsNumber: true })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="preOpDays" className="label">
                                            Pre-Op Days
                                        </label>
                                        <input
                                            id="preOpDays"
                                            type="number"
                                            min="0"
                                            className="input"
                                            {...register('preOpDays', { valueAsNumber: true })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="postOpDays" className="label">
                                            Post-Op Days
                                        </label>
                                        <input
                                            id="postOpDays"
                                            type="number"
                                            min="0"
                                            className="input"
                                            {...register('postOpDays', { valueAsNumber: true })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="defaultIcuDays" className="label">
                                            ICU Days
                                        </label>
                                        <input
                                            id="defaultIcuDays"
                                            type="number"
                                            min="0"
                                            className="input"
                                            {...register('defaultIcuDays', { valueAsNumber: true })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="card-footer flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="btn btn-secondary btn-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createMutation.isPending || updateMutation.isPending}
                                    className="btn btn-primary btn-md"
                                >
                                    {createMutation.isPending || updateMutation.isPending
                                        ? 'Saving...'
                                        : editingProcedure
                                            ? 'Update Procedure'
                                            : 'Add Procedure'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
